import { css, type Interpolation, type Theme } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { LAYOUT2, TYPOGRAPHY2 } from '../Styles';

const ICON_SIZE = 24;
const ICON_BUTTON_SIZE = 44;
const DRAWER_WIDTH = 500;

const OVERLAY_STYLE = css`
  label: OVERLAY_STYLE;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1199;
  opacity: 0;
  animation: fadeIn 0.2s ease-out forwards;

  &.closing {
    animation: fadeOut 0.2s ease-out forwards;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
`;

const CONTENT_WRAPPER_STYLE = css`
  label: CONTENT_WRAPPER_STYLE;
  flex: 1;
  padding-top: ${LAYOUT2.spacing2}px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
`;

const DRAWER_STYLE = css`
  label: DRAWER_STYLE;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${DRAWER_WIDTH}px;
  background-color: var(--background);
  border-left: 1px solid var(--border2);
  padding: 0 ${LAYOUT2.spacing1p5}px ${LAYOUT2.spacing1p5}px ${LAYOUT2.spacing1p5}px;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 8px rgba(16, 4, 90, 0.1);
  z-index: 1200;
  transform: translateX(100%);
  animation: slideIn 0.2s ease-out forwards;

  &.closing {
    animation: slideOut 0.2s ease-out forwards;
  }

  @keyframes slideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }

  @keyframes slideOut {
    from { transform: translateX(0); }
    to { transform: translateX(100%); }
  }

`;

const FOOTER_STYLE = css`
  label: FLYOUT_FOOTER;
  padding: ${LAYOUT2.spacing2}px;
  border-top: 1px solid var(--border2);
`;

const FOOTER_BORDERLESS_STYLE = css`
  label: FLYOUT_FOOTER_BORDERLESS;
  border-top: 0;
`;

const CLOSE_BUTTON_STYLE = css`
  label: CLOSE_BUTTON_STYLE;
  margin-right: -${LAYOUT2.spacing1}px;
`;

const HEADER_STYLE = css`
  label: HEADER_STYLE;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: ${LAYOUT2.spacing6}px;
  border-bottom: 1px solid var(--border2);
  ${TYPOGRAPHY2.largeBold}

  button {
    color: var(--text);
    cursor: pointer;
    height: ${ICON_BUTTON_SIZE}px;
    width: ${ICON_BUTTON_SIZE}px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    padding: 0;
    flex: 0 0 ${ICON_BUTTON_SIZE}px;
  }
`;

const HEADER_TITLE_CONTAINER_STYLE = css`
  label: HEADER_TITLE_CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacingP5}px;
`;

const HEADER_TITLE_CONTENT_STYLE = css`
  label: HEADER_TITLE_CONTENT;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  color: var(--text);
`;

const HEADER_SUBHEADER_STYLE = css`
  label: HEADER_SUBHEADER_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--text2);
`;

export const FlyOut = ({
  children,
  closeButtonCss,
  contentCss,
  drawerCss,
  footer,
  footerBorder = true,
  footerCss,
  headerCss,
  subheader,
  onClose,
  open,
  flyOutClosed,
  title,
}: {
  children: React.ReactNode;
  closeButtonCss?: Interpolation<Theme>;
  contentCss?: Interpolation<Theme>;
  drawerCss?: Interpolation<Theme>;
  footer?: React.ReactNode;
  footerBorder?: boolean;
  footerCss?: Interpolation<Theme>;
  headerCss?: Interpolation<Theme>;
  subheader?: React.ReactNode;
  onClose?: () => void;
  open: boolean;
  flyOutClosed: () => void;
  title: React.ReactNode;
}) => {
  const [isClosing, setIsClosing] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setIsVisible(true);
      setIsClosing(false);
    } else if (isVisible) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open, isVisible]);

  const handleClose = () => {
    flyOutClosed();
    if (onClose) {
      onClose();
    }
  };

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        css={OVERLAY_STYLE}
        className={isClosing ? 'closing' : ''}
        onClick={handleOverlayClick}
      />
      <div css={[DRAWER_STYLE, drawerCss]} className={isClosing ? 'closing' : ''}>
        <div css={[HEADER_STYLE, headerCss]}>
          <div css={HEADER_TITLE_CONTAINER_STYLE}>
            <div css={HEADER_TITLE_CONTENT_STYLE}>{title}</div>
            {subheader && (
              <div css={HEADER_SUBHEADER_STYLE}>
                {subheader}
              </div>
            )}
          </div>
          <button onClick={handleClose} css={[CLOSE_BUTTON_STYLE, closeButtonCss]}>
            <CloseIcon sx={{ fontSize: `${ICON_SIZE}px !important`, width: ICON_SIZE, height: ICON_SIZE }} />
          </button>
        </div>
        <div css={[CONTENT_WRAPPER_STYLE, contentCss]}>{children}</div>

        {footer && <footer css={[FOOTER_STYLE, !footerBorder && FOOTER_BORDERLESS_STYLE, footerCss]}>{footer}</footer>}
      </div>
    </>
  );
};
